import { type ApiClient } from '@klareospace/pharos-lib-front-utils';
import type {
  CreateOrderType,
  OrderType,
  PharosApiResponse,
  PharosPaginatedResponse,
  UpdateOrderType,
} from '@klareospace/pharos-lib-types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SearchOrderTypesParams {
  page?: number;
  pageSize?: number;
  statuses?: string[];
}

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createOrderTypeApi(client: ApiClient): {
  archiveOrderType: (id: string) => Promise<OrderType>;
  createOrderType: (data: CreateOrderType) => Promise<OrderType>;
  searchOrderTypes: (params?: SearchOrderTypesParams) => Promise<PharosPaginatedResponse<OrderType>>;
  updateOrderType: (id: string, data: UpdateOrderType) => Promise<OrderType>;
} {
  return {
    archiveOrderType: async (id: string): Promise<OrderType> => {
      const response = await client.execute<PharosApiResponse<OrderType>>(() =>
        client.instance.delete(`/order-types/${id}`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    createOrderType: async (data: CreateOrderType): Promise<OrderType> => {
      const response = await client.execute<PharosApiResponse<OrderType>>(() =>
        client.instance.post('/order-types', data),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    searchOrderTypes: async (params?: SearchOrderTypesParams): Promise<PharosPaginatedResponse<OrderType>> => {
      const query: Record<string, number> = {};
      if (params?.page != null) query.page = params.page;
      if (params?.pageSize != null) query.limit = params.pageSize;
      const body: Record<string, unknown> = {};
      if (params?.statuses && params.statuses.length > 0) {
        body.statuses = params.statuses;
      }
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<OrderType>>>(() =>
        client.instance.post('/order-types/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    updateOrderType: async (id: string, data: UpdateOrderType): Promise<OrderType> => {
      const response = await client.execute<PharosApiResponse<OrderType>>(() =>
        client.instance.patch(`/order-types/${id}`, data),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },
  };
}
